import React from 'react';
import { Link } from 'react-router-dom';
import LegalPolicyLayout from '@/components/legal/LegalPolicyLayout';
import { LEGAL_ENTITY, LEGAL_ROUTES } from '@/constants/legalEntity';

export const RefundPolicy: React.FC = () => {
  return (
    <LegalPolicyLayout title="Refund Policy">
      <section className="mb-8">
        <h2>1. Overview</h2>
        <p>
          This Refund Policy applies to purchases made through {LEGAL_ENTITY.productName}, operated by{' '}
          {LEGAL_ENTITY.legalNameEn}. Payments are processed by our payment partner Lemon Squeezy (merchant of
          record for applicable charges). This policy works together with our{' '}
          <Link to={LEGAL_ROUTES.terms}>Terms of Service</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2>2. Who this policy covers</h2>
        <ul>
          <li>
            <strong>Students</strong> — lesson package subscriptions and one-time AI practice purchases.
          </li>
          <li>
            <strong>Teachers / music schools</strong> — platform access billed on a pay-as-you-go basis (see
            §5).
          </li>
        </ul>
      </section>

      <section className="mb-8">
        <h2>3. Fourteen (14) day refund — students (subscriptions &amp; AI purchases)</h2>
        <p>
          If you are a <strong>student</strong> and you purchased a <strong>lesson subscription</strong> or a{' '}
          <strong>one-time AI practice feature</strong> through {LEGAL_ENTITY.productName}, you may request a{' '}
          <strong>full refund within 14 calendar days</strong> of the purchase date, provided that:
        </p>
        <ul>
          <li>
            You submit the refund request <strong>inside the app</strong> (billing / account area) or by email
            to {LEGAL_ENTITY.contactEmail} from the email on your account; and
          </li>
          <li>
            You have <strong>not started using</strong> the paid service. For lesson subscriptions, “use”
            means booking or attending a lesson in the <strong>current billing period</strong> (pending,
            confirmed, or completed bookings count). Cancelling renewal alone does not qualify for a refund
            once a lesson has been booked. For AI features, “use” includes
            uploading practice audio, receiving AI feedback, or spending AI credits from that purchase.
          </li>
        </ul>
        <p>
          If any paid service was used, the 14-day full refund does not apply. Partial refunds for unused
          periods may be considered only where required by law or at the teacher’s discretion for lesson
          packages they sell.
        </p>
      </section>

      <section className="mb-8">
        <h2>4. How to request a refund</h2>
        <ol>
          <li>Sign in to your student account in the app.</li>
          <li>Open billing or subscription settings and use the refund request option, or email us with your account email, purchase date, and order reference if shown.</li>
          <li>We will confirm eligibility (14-day window, no use of service) and process approved refunds via Lemon Squeezy to your original payment method.</li>
        </ol>
        <p>We aim to respond within 5 business days and to complete approved refunds within 14 business days.</p>
      </section>

      <section className="mb-8">
        <h2>5. Teachers — pay-as-you-go platform fees</h2>
        <p>
          Teachers use {LEGAL_ENTITY.productName} on a <strong>pay-as-you-go (PAYG)</strong> basis: there is no
          large upfront platform subscription. Platform fees are calculated on student payments processed
          through the platform (see <Link to="/pricing">Pricing</Link>). Because billing is usage-based at
          period end, refunds for platform fees generally apply only to clear billing errors or duplicate
          charges. Contact {LEGAL_ENTITY.contactEmail} with details.
        </p>
      </section>

      <section className="mb-8">
        <h2>6. Cancellations vs refunds</h2>
        <p>
          Cancelling a subscription stops <strong>future</strong> renewals; it does not automatically refund
          the current period unless you qualify under §3. You may cancel from the app or via the Lemon Squeezy
          customer portal link we provide after checkout.
        </p>
      </section>

      <section className="mb-8">
        <h2>7. Chargebacks</h2>
        <p>
          If you have a problem with a charge, contact us first so we can resolve it. Unauthorized chargebacks
          may result in account suspension while the dispute is reviewed.
        </p>
      </section>

      <section className="mb-8">
        <h2>8. Contact</h2>
        <p>
          {LEGAL_ENTITY.legalNameEn}
          <br />
          Email: {LEGAL_ENTITY.contactEmail}
          <br />
          Product: {LEGAL_ENTITY.productName} ({LEGAL_ENTITY.domain})
        </p>
      </section>
    </LegalPolicyLayout>
  );
};
